// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.
import React, { useState, useRef } from 'react';
import { Modal, Box } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useAppDispatch } from '../../../../store';
import { updateCandidateInterviewConfiguration } from '../../../../store/interviewHostSlice';
import styles from './candidates.module.scss';

type DeliveryChannel = 'interview_host' | 'ai_skill_assessment';

interface CandidateInterviewConfig {
  duration_minutes: number;
  language: string;
  mandatory_questions: number;
  focus_areas: string[];
  off_limit_topics: string[];
  delivery_channels?: DeliveryChannel[];
}

interface ConfigureAssessmentModalProps {
  open: boolean;
  onClose: () => void;
  candidateId: string;
  jobId: string;
  candidateName: string;
  roleTitle: string;
  currentConfig: CandidateInterviewConfig;
  currentContext: string;
  onSaved: () => void;
  onShowToast: (message: string, type?: 'success' | 'error') => void;
}

const DURATION_OPTIONS = [20, 25, 30];
const MAX_CONTEXT_LENGTH = 500;
const REQUIRED_FOCUS_AREAS = 5;
// Backend defaults the skill assessment deadline to 48 hours if none is supplied,
// and the assessment agent runs independently (candidate picks their own level).
const SKILL_ASSESSMENT_DEADLINE_HOURS = 48;
const MAX_FILE_SIZE_MB = 2;
const ACCEPTED_FILE_TYPES = '.txt,.md,.pdf,.doc,.docx';

type ContextInputMode = 'text' | 'file';

const ConfigureAssessmentModal: React.FC<ConfigureAssessmentModalProps> = ({
  open,
  onClose,
  candidateId,
  jobId,
  candidateName,
  roleTitle,
  currentConfig,
  currentContext,
  onSaved,
  onShowToast
}) => {
  const dispatch = useAppDispatch();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const initialChannels = currentConfig.delivery_channels?.length
    ? currentConfig.delivery_channels
    : ['interview_host'];
  const [includeInterviewHost, setIncludeInterviewHost] = useState(initialChannels.includes('interview_host'));
  const [includeSkillAssessment, setIncludeSkillAssessment] = useState(initialChannels.includes('ai_skill_assessment'));
  const [interviewContext, setInterviewContext] = useState(currentContext);
  const [contextInputMode, setContextInputMode] = useState<ContextInputMode>('text');
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [duration, setDuration] = useState(currentConfig.duration_minutes);
  const [language, setLanguage] = useState(currentConfig.language);
  const [focusAreas, setFocusAreas] = useState<string[]>(currentConfig.focus_areas);
  const [newFocusAreaInput, setNewFocusAreaInput] = useState('');
  const [saving, setSaving] = useState(false);

  const contextCharCount = interviewContext.length;
  const focusAreaCount = focusAreas.length;
  const isExactCount = focusAreaCount === REQUIRED_FOCUS_AREAS;
  const canAddMoreFocusAreas = focusAreaCount < REQUIRED_FOCUS_AREAS;
  const needsMoreFocusAreas = focusAreaCount < REQUIRED_FOCUS_AREAS;

  const handleAddFocusArea = (): void => {
    const trimmed = newFocusAreaInput.trim();
    if (!trimmed || !canAddMoreFocusAreas) {
      return;
    }
    if (focusAreas.some((area) => area.toLowerCase() === trimmed.toLowerCase())) {
      return;
    }
    setFocusAreas((prev) => [...prev, trimmed]);
    setNewFocusAreaInput('');
  };

  const handleRemoveFocusArea = (index: number): void => {
    setFocusAreas((prev) => prev.filter((_, i) => i !== index));
  };

  const handleToggleInterviewHost = (): void => {
    if (includeInterviewHost && !includeSkillAssessment) {
      onShowToast('At least one of Interview Host or AI Skill Assessment must be selected', 'error');
      return;
    }
    setIncludeInterviewHost((prev) => !prev);
  };

  const handleToggleSkillAssessment = (): void => {
    if (includeSkillAssessment && !includeInterviewHost) {
      onShowToast('At least one of Interview Host or AI Skill Assessment must be selected', 'error');
      return;
    }
    setIncludeSkillAssessment((prev) => !prev);
  };

  const handleContextChange = (e: React.ChangeEvent<HTMLTextAreaElement>): void => {
    const value = e.target.value;
    if (value.length <= MAX_CONTEXT_LENGTH) {
      setInterviewContext(value);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file size
    const fileSizeMB = file.size / (1024 * 1024);
    if (fileSizeMB > MAX_FILE_SIZE_MB) {
      onShowToast(`File size exceeds ${MAX_FILE_SIZE_MB}MB limit`, 'error');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    setUploadedFile(file);
    onShowToast('File uploaded successfully', 'success');
  };

  const handleRemoveFile = (): void => {
    setUploadedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleModeChange = (mode: ContextInputMode): void => {
    if (mode === contextInputMode) return;

    // Warn if switching modes with content
    const hasContent = contextInputMode === 'text' ? interviewContext.trim() : uploadedFile !== null;
    if (hasContent) {
      const confirmSwitch = window.confirm(
        'Switching input mode will clear your current content. Do you want to continue?'
      );
      if (!confirmSwitch) return;
    }

    setContextInputMode(mode);
    setInterviewContext('');
    setUploadedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddFocusArea();
    }
  };

  const handleSave = async (): Promise<void> => {
    if (!includeInterviewHost && !includeSkillAssessment) {
      onShowToast('At least one of Interview Host or AI Skill Assessment must be selected', 'error');
      return;
    }

    if (includeInterviewHost && focusAreaCount !== REQUIRED_FOCUS_AREAS) {
      onShowToast(
        needsMoreFocusAreas
          ? `Please add ${REQUIRED_FOCUS_AREAS - focusAreaCount} more focus area${REQUIRED_FOCUS_AREAS - focusAreaCount > 1 ? 's' : ''} (${REQUIRED_FOCUS_AREAS} required)`
          : `Please remove ${focusAreaCount - REQUIRED_FOCUS_AREAS} focus area${focusAreaCount - REQUIRED_FOCUS_AREAS > 1 ? 's' : ''} (${REQUIRED_FOCUS_AREAS} required)`,
        'error'
      );
      return;
    }

    setSaving(true);

    try {
      const deliveryChannels: DeliveryChannel[] = [
        ...(includeInterviewHost ? (['interview_host'] as const) : []),
        ...(includeSkillAssessment ? (['ai_skill_assessment'] as const) : [])
      ];

      await dispatch(
        updateCandidateInterviewConfiguration({
          candidateId,
          jobId,
          interviewContext: contextInputMode === 'text' ? interviewContext : undefined,
          interviewContextFile: contextInputMode === 'file' ? uploadedFile || undefined : undefined,
          interviewConfiguration: {
            duration_minutes: duration,
            language,
            mandatory_questions: currentConfig.mandatory_questions,
            focus_areas: focusAreas,
            off_limit_topics: currentConfig.off_limit_topics,
            delivery_channels: deliveryChannels
          }
        })
      ).unwrap();

      onSaved();
      onClose();
    } catch (err) {
      console.error('Failed to save assessment configuration:', err);
      onShowToast(typeof err === 'string' ? err : 'Could not save the assessment configuration.', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box className={styles.modal}>
        {/* Header */}
        <div className={styles.modalHeader}>
          <div className={styles.modalTitleWrapper}>
            <div className={styles.modalIcon}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <div>
              <h2 className={styles.modalTitle}>Configure Assessment</h2>
              <p className={styles.modalSubtitle}>
                {candidateName} · {roleTitle}
              </p>
            </div>
          </div>
          <button className={styles.modalClose} onClick={onClose} aria-label="Close">
            <CloseIcon />
          </button>
        </div>

        {/* Body */}
        <div className={styles.modalBody}>
          <label className={styles.sectionQuestion}>WHAT WOULD YOU LIKE TO SEND THIS CANDIDATE?</label>
          <div className={styles.assessmentCardsRow}>
            {/* Interview Host card */}
            <button
              type="button"
              className={`${styles.assessmentCard} ${includeInterviewHost ? styles.assessmentCardSelected : ''}`}
              onClick={handleToggleInterviewHost}
            >
              <span
                className={`${styles.assessmentCardCheck} ${includeInterviewHost ? styles.assessmentCardCheckActive : ''}`}
              >
                <svg viewBox="0 0 16 16" fill="currentColor">
                  <path
                    d="M13.5 4.5l-7 7-3-3"
                    stroke="currentColor"
                    strokeWidth="2"
                    fill="none"
                    strokeLinecap="round"
                  />
                </svg>
              </span>
              <span className={styles.assessmentCardBody}>
                <p className={styles.assessmentCardTitle}>Interview Host</p>
                <p className={styles.assessmentCardDesc}>Live AI-conducted voice interview</p>
              </span>
            </button>

            {/* AI Skill Assessment card */}
            <button
              type="button"
              className={`${styles.assessmentCard} ${includeSkillAssessment ? styles.assessmentCardSelected : ''}`}
              onClick={handleToggleSkillAssessment}
            >
              <span
                className={`${styles.assessmentCardCheck} ${includeSkillAssessment ? styles.assessmentCardCheckActive : ''}`}
              >
                <svg viewBox="0 0 16 16" fill="currentColor">
                  <path
                    d="M13.5 4.5l-7 7-3-3"
                    stroke="currentColor"
                    strokeWidth="2"
                    fill="none"
                    strokeLinecap="round"
                  />
                </svg>
              </span>
              <span className={styles.assessmentCardBody}>
                <p className={styles.assessmentCardTitle}>AI Skill Assessment</p>
                <p className={styles.assessmentCardDesc}>AI-specific skills test</p>
              </span>
            </button>
          </div>

          {includeInterviewHost && includeSkillAssessment && (
            <div className={styles.infoBanner}>
              <InfoOutlinedIcon className={styles.infoBannerIcon} />
              <span>
                <strong>Both selected</strong> — the interview invite expires in <strong>72 hours</strong>. Once the
                candidate completes the Interview Host session, the AI Skill Assessment link is sent automatically, with{' '}
                <strong>{SKILL_ASSESSMENT_DEADLINE_HOURS} hours</strong> to complete it.
              </span>
            </div>
          )}

          {/* Interview Host Settings */}
          {includeInterviewHost && (
            <>
              <div className={`${styles.assessmentSectionHeader} ${styles.assessmentSectionHeaderGreen}`}>
                <span className={styles.assessmentSectionIcon}>🎙️</span>
                Interview Host Settings
              </div>

              <div className={styles.formGroup}>
                <label className={styles.formSectionLabel}>INTERVIEW CONTEXT</label>

                {/* Mode Selector Tabs */}
                <div className={styles.contextModeTabs}>
                  <button
                    type="button"
                    className={`${styles.contextModeTab} ${contextInputMode === 'text' ? styles.contextModeTabActive : ''}`}
                    onClick={() => handleModeChange('text')}
                  >
                    Text
                  </button>
                  <button
                    type="button"
                    className={`${styles.contextModeTab} ${contextInputMode === 'file' ? styles.contextModeTabActive : ''}`}
                    onClick={() => handleModeChange('file')}
                  >
                    <UploadFileIcon sx={{ fontSize: 16, marginRight: '4px' }} />
                    Upload File
                  </button>
                </div>

                {/* Text Input Mode */}
                {contextInputMode === 'text' && (
                  <>
                    <textarea
                      className={styles.formTextareaNew}
                      value={interviewContext}
                      onChange={handleContextChange}
                      rows={4}
                      placeholder="Enter any context about the role, team, priorities, or specific areas..."
                    />
                    <div className={styles.charCount}>
                      {contextCharCount}/{MAX_CONTEXT_LENGTH}
                    </div>
                  </>
                )}

                {/* File Upload Mode */}
                {contextInputMode === 'file' && (
                  <div className={styles.fileUploadContainer}>
                    {!uploadedFile ? (
                      <>
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept={ACCEPTED_FILE_TYPES}
                          onChange={handleFileSelect}
                          style={{ display: 'none' }}
                          id="context-file-input"
                        />
                        <label htmlFor="context-file-input" className={styles.fileUploadLabel}>
                          <UploadFileIcon sx={{ fontSize: 32, marginBottom: '8px', opacity: 0.6 }} />
                          <span className={styles.fileUploadText}>Click to upload or drag and drop</span>
                          <span className={styles.fileUploadHint}>
                            Supported: .txt, .md, .pdf, .doc, .docx (max {MAX_FILE_SIZE_MB}MB)
                          </span>
                        </label>
                      </>
                    ) : (
                      <div className={styles.uploadedFileCard}>
                        <div className={styles.uploadedFileInfo}>
                          <UploadFileIcon sx={{ fontSize: 24, marginRight: '12px', color: '#10b981' }} />
                          <div className={styles.uploadedFileDetails}>
                            <span className={styles.uploadedFileName}>{uploadedFile.name}</span>
                            <span className={styles.uploadedFileStats}>{(uploadedFile.size / 1024).toFixed(2)} KB</span>
                          </div>
                        </div>
                        <button
                          type="button"
                          className={styles.removeFileBtn}
                          onClick={handleRemoveFile}
                          aria-label="Remove file"
                        >
                          <DeleteOutlineIcon sx={{ fontSize: 20 }} />
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className={styles.configRow}>
                <div className={styles.configCol}>
                  <label className={styles.formSectionLabel}>DURATION (MINUTES)</label>
                  <div className={styles.durationSelector}>
                    {DURATION_OPTIONS.map((option) => (
                      <button
                        key={option}
                        type="button"
                        className={`${styles.durationOption} ${duration === option ? styles.durationOptionActive : ''}`}
                        onClick={() => setDuration(option)}
                      >
                        {option}
                      </button>
                    ))}
                  </div>
                </div>

                <div className={styles.configCol}>
                  <label className={styles.formSectionLabel}>LANGUAGE</label>
                  <select
                    className={styles.formSelectNew}
                    value={language}
                    onChange={(e) => setLanguage(e.target.value)}
                  >
                    <option value="English">English</option>
                  </select>
                </div>
              </div>

              {focusAreaCount > 0 && (
                <div className={styles.formGroup}>
                  <label className={styles.formSectionLabel}>
                    FOCUS AREAS ({focusAreaCount}/{REQUIRED_FOCUS_AREAS})
                    {!isExactCount && (
                      <span style={{ color: needsMoreFocusAreas ? '#f97316' : '#ef4444', marginLeft: '8px' }}>
                        •{' '}
                        {needsMoreFocusAreas
                          ? `${REQUIRED_FOCUS_AREAS - focusAreaCount} more required`
                          : `Remove ${focusAreaCount - REQUIRED_FOCUS_AREAS} to meet requirement`}
                      </span>
                    )}
                    {isExactCount && <span style={{ color: '#10b981', marginLeft: '8px' }}>• Complete</span>}
                  </label>
                  <div className={styles.focusAreasGrid}>
                    {focusAreas.map((area, index) => (
                      <div key={index} className={styles.focusAreaChip}>
                        <span>{area}</span>
                        <button
                          type="button"
                          className={styles.removeFocusAreaBtn}
                          onClick={() => handleRemoveFocusArea(index)}
                          aria-label={`Remove ${area}`}
                        >
                          <CloseIcon sx={{ fontSize: 16 }} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className={styles.formGroup}>
                <label className={styles.formSectionLabel}>
                  {focusAreaCount === 0
                    ? `ADD FOCUS AREAS (Exactly ${REQUIRED_FOCUS_AREAS} required)`
                    : isExactCount
                      ? 'ADD FOCUS AREA (Complete)'
                      : `ADD FOCUS AREA (${canAddMoreFocusAreas ? `${REQUIRED_FOCUS_AREAS - focusAreaCount} remaining` : `Remove ${focusAreaCount - REQUIRED_FOCUS_AREAS} extra`})`}
                </label>
                <div className={styles.addFocusAreaRow}>
                  <input
                    type="text"
                    className={styles.formInputNew}
                    value={newFocusAreaInput}
                    onChange={(e) => setNewFocusAreaInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder={
                      needsMoreFocusAreas
                        ? `Add ${REQUIRED_FOCUS_AREAS - focusAreaCount} more (${REQUIRED_FOCUS_AREAS} required)`
                        : `${REQUIRED_FOCUS_AREAS} focus areas required`
                    }
                    disabled={!canAddMoreFocusAreas}
                  />
                  <button
                    type="button"
                    className={styles.btnAddFocusArea}
                    onClick={handleAddFocusArea}
                    disabled={!canAddMoreFocusAreas || !newFocusAreaInput.trim()}
                  >
                    <AddIcon sx={{ fontSize: 18 }} />
                    Add
                  </button>
                </div>
              </div>
            </>
          )}

          {/* AI Skill Assessment */}
          {includeSkillAssessment && (
            <>
              <div className={`${styles.assessmentSectionHeader} ${styles.assessmentSectionHeaderGreen}`}>
                <span className={styles.assessmentSectionIcon}>📝</span>
                AI Skill Assessment
              </div>
              <div className={styles.infoBanner}>
                <InfoOutlinedIcon className={styles.infoBannerIcon} />
                <div style={{ flex: 1 }}>
                  <p style={{ margin: 0 }}>
                    Nothing to configure here — the assessment agent runs independently. The candidate selects their own
                    level when they open the link, and the agent generates questions accordingly.
                  </p>
                  <p style={{ margin: '0.5rem 0 0 0' }}>
                    Candidate has <strong>{SKILL_ASSESSMENT_DEADLINE_HOURS} hours</strong> to complete it from the time
                    the link is sent. The report shows only the skill score, strengths, and gaps.
                  </p>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className={styles.modalFooter}>
          <button className={styles.btnSecondary} onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button
            className={styles.btnPrimary}
            onClick={handleSave}
            disabled={
              saving || (!includeInterviewHost && !includeSkillAssessment) || (includeInterviewHost && !isExactCount)
            }
            title={
              !includeInterviewHost && !includeSkillAssessment
                ? 'Select at least one of Interview Host or AI Skill Assessment'
                : includeInterviewHost && !isExactCount
                  ? `Exactly ${REQUIRED_FOCUS_AREAS} focus areas are required`
                  : undefined
            }
          >
            {saving ? 'Saving...' : 'Save configuration'}
          </button>
        </div>
      </Box>
    </Modal>
  );
};

export default ConfigureAssessmentModal;
